import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PromotionBadge, { badgeFor } from './PromotionBadge';
import PromotionDetails from './PromotionDetails';
import { CloseIcon } from '../BookingNext/ServicesStep/icons';
import { getCMSJWT } from '../../services/auth.service';
import { promotionService } from '../../services/promotion.service';
import { storeService } from '../../services/store.service';
import { POPUP_CLAIMED, popupDoes, popupWhen } from '../../utils/promotionPopup';
import type { Promotion } from '../../types/promotion.types';
import styles from './Promotions.module.css';

/** Fired by App when a visitor signs in, so a claim waiting on sign-in goes through. */
export const SIGNED_IN_EVENT = 'mpg:signed-in';

/**
 * The promotion pop-up (board SF 02): on the home page, one live offer the
 * owner ticked "Pop-up" for, that this visitor hasn't claimed or put off
 * with "Not now" (remembered on this device per offer). A bottom sheet (a
 * dialog in the middle on a computer): the badge large on its tint, the
 * title, until when, one line of what it does, "See terms" (the details
 * sheet, SF 05b), then Claim offer and Not now. Claim signs them in first
 * when needed, then claims. The salon's server decides whether they may
 * (CMS G11); its reason is shown when they may not.
 */
const PromotionPopup: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<Promotion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [terms, setTerms] = useState(false);
  const onHome = location.pathname === '/';

  const claimPending = useCallback(async () => {
    const pending = promotionService.pendingClaim();
    if (!pending || !getCMSJWT()) return;
    promotionService.claimAfterSignIn(null);
    try {
      const p = await promotionService.claim(pending);
      setOffer(p);
      setClaimed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That offer couldn't be claimed.");
    }
  }, []);

  useEffect(() => {
    window.addEventListener(SIGNED_IN_EVENT, claimPending);
    return () => window.removeEventListener(SIGNED_IN_EVENT, claimPending);
  }, [claimPending]);

  useEffect(() => {
    if (!onHome) {
      setOffer(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      // A multi-location business asks where first; the offer waits.
      if (Number(localStorage.getItem('storeCount') ?? '0') > 1 && !localStorage.getItem('selectedStore')) return;
      let companyId: number;
      try {
        companyId = Number(await storeService.getCompanyId());
      } catch {
        return;
      }
      if (!companyId) return;
      const [catalog, mine] = await Promise.all([promotionService.getCatalog(companyId), promotionService.getMine()]);
      if (cancelled) return;
      const dismissed = new Set(promotionService.dismissed());
      const yours = new Set(mine.filter((p) => p.yours).map((p) => p.id));
      const next = catalog.find((p) => p.showPopup && !dismissed.has(p.id) && !yours.has(p.id));
      if (next) {
        setError(null);
        setClaimed(false);
        setTerms(false);
        setOffer(next);
      }
    }, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [onHome]);

  if (!offer) return null;

  const close = () => {
    if (!claimed) promotionService.dismiss(offer.id);
    setOffer(null);
  };

  const claim = async () => {
    if (!getCMSJWT()) {
      // Sign in first; App fires SIGNED_IN_EVENT and the claim goes through.
      promotionService.claimAfterSignIn(offer.id);
      promotionService.dismiss(offer.id);
      setOffer(null);
      window.dispatchEvent(new Event('mpg:open-login'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await promotionService.claim(offer.id);
      setClaimed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That offer couldn't be claimed.");
    } finally {
      setBusy(false);
    }
  };

  const bookNow = () => {
    setOffer(null);
    navigate('/book/services');
  };

  return (
    <div className={styles.dScrim} role="presentation" onClick={close}>
      <div
        className={styles.popSheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.dGrab} />
        <div className={styles.popPanel} style={{ background: badgeFor(offer.icon).tint }}>
          <PromotionBadge icon={offer.icon} size={100} />
          <button type="button" className={styles.popX} aria-label="Close" onClick={close}>
            <CloseIcon size={18} />
          </button>
        </div>
        <h2 id="promo-popup-title" className={styles.popHeading}>
          {offer.title}
        </h2>
        <span className={styles.popWhen}>{popupWhen(offer)}</span>
        <span className={styles.popDoes}>{claimed ? POPUP_CLAIMED : popupDoes(offer)}</span>
        <button type="button" className={styles.popTerms} onClick={() => setTerms(true)}>
          See terms
        </button>
        {error && <span className={styles.error}>{error}</span>}
        <div className={styles.popActions}>
          {claimed ? (
            <button type="button" className={styles.popClaim} onClick={bookNow}>
              Book now
            </button>
          ) : (
            <>
              <button type="button" className={styles.popClaim} disabled={busy} onClick={() => void claim()}>
                {busy ? 'Claiming…' : 'Claim offer'}
              </button>
              <button type="button" className={styles.popNotNow} onClick={close}>
                Not now
              </button>
            </>
          )}
        </div>
      </div>
      {terms && (
        <div onClick={(e) => e.stopPropagation()}>
          <PromotionDetails
            promotion={claimed ? { ...offer, yours: true, claimed: true } : offer}
            onClose={() => setTerms(false)}
            primary={
              claimed
                ? { label: 'Book now', onClick: bookNow }
                : { label: 'Claim offer', onClick: () => void claim().then(() => setTerms(false)), busy }
            }
          />
        </div>
      )}
    </div>
  );
};

export default PromotionPopup;
